import axios from 'axios';
import { DIGISAKA_API_ORIGIN } from '../../../config/digisakaApiOrigin';
import { DIGISAKA_OUTBOUND_API_ORIGIN, getDigisakaAuthHeaders } from '../../../config/digisakaApi';

export type FarmIndexAlertRecord = {
  alert_message: string;
  observation_date?: string | null;
  ndvi_value?: number | null;
  ndmi_value?: number | null;
};

export type FarmForecastAlertRecord = {
  id: number;
  farm_id: string;
  alert_category: string;
  alert_type: string;
  severity: string;
  alert_message: string;
  forecast_date: string;
  data_value?: number | null;
  timestamp?: string | null;
};

export type FarmForecastAlertsResponse = {
  farm_id: string;
  ndvi_forecast: FarmForecastAlertRecord[];
  ndmi_forecast: FarmForecastAlertRecord[];
  weather_risk: FarmForecastAlertRecord[];
};

const EMPTY_FORECAST_ALERTS = (
  farmId: number | string
): FarmForecastAlertsResponse => ({
  farm_id: String(farmId),
  ndvi_forecast: [],
  ndmi_forecast: [],
  weather_risk: [],
});

export async function fetchFarmForecastAlerts(
  farmId: number | string,
  headers: Record<string, string>
): Promise<FarmForecastAlertsResponse> {
  const id = encodeURIComponent(String(farmId));
  try {
    const res = await axios.get<FarmForecastAlertsResponse>(
      `${DIGISAKA_API_ORIGIN}/api/farm-forecast-alerts/${id}/`,
      { headers, withCredentials: true }
    );
    const data = res.data;
    if (!data) return EMPTY_FORECAST_ALERTS(farmId);
    return normalizeForecastAlertsResponse({
      farm_id: data.farm_id ?? String(farmId),
      ndvi_forecast: Array.isArray(data.ndvi_forecast) ? data.ndvi_forecast : [],
      ndmi_forecast: Array.isArray(data.ndmi_forecast) ? data.ndmi_forecast : [],
      weather_risk: Array.isArray(data.weather_risk) ? data.weather_risk : [],
    });
  } catch (e: unknown) {
    if (axios.isAxiosError(e) && e.response?.status === 404) {
      return EMPTY_FORECAST_ALERTS(farmId);
    }
    console.error('Farm forecast alerts request failed:', farmId, e);
    return EMPTY_FORECAST_ALERTS(farmId);
  }
}

export function countFarmForecastAlerts(data: FarmForecastAlertsResponse | null): number {
  if (!data) return 0;
  return (
    actionableForecastAlerts(data.ndvi_forecast || []).length +
    actionableForecastAlerts(data.ndmi_forecast || []).length +
    actionableForecastAlerts(data.weather_risk || []).length
  );
}

export function forecastAlertTitle(alert: FarmForecastAlertRecord): string {
  const cat = (alert.alert_category || '').toLowerCase();
  const type = (alert.alert_type || '').toLowerCase();
  if (type === 'forecast_trend' || type === 'rain_trend' || type === 'heat_trend') {
    return 'Forecast trend';
  }
  if (cat === 'ndvi_forecast') return 'Vegetation forecast';
  if (cat === 'ndmi_forecast') return 'Moisture forecast';
  if (type === 'weather_rain') return 'Rain outlook';
  if (type === 'weather_heat') return 'Heat outlook';
  if (type === 'weather_drought') return 'Dry spell outlook';
  return 'Weather forecast';
}

export function forecastAlertIcon(alert: FarmForecastAlertRecord): string {
  const cat = (alert.alert_category || '').toLowerCase();
  const type = (alert.alert_type || '').toLowerCase();
  if (cat === 'ndvi_forecast') return 'ri-leaf-line';
  if (cat === 'ndmi_forecast') return 'ri-drop-fill';
  if (type === 'weather_heat') return 'ri-temp-hot-line';
  if (type === 'weather_drought') return 'ri-sun-line';
  return 'ri-cloud-rain-line';
}

export function forecastAlertVariant(
  alert: FarmForecastAlertRecord
): 'vegetation' | 'moisture' | 'weather' {
  const cat = (alert.alert_category || '').toLowerCase();
  if (cat === 'ndmi_forecast') return 'moisture';
  if (cat === 'weather_risk') return 'weather';
  return 'vegetation';
}

export function formatForecastAlertDate(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const day = String(raw).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const parsed = new Date(`${day}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

/** Days from local today to forecast date (can be negative if past). */
export function forecastDaysFromToday(raw: string | null | undefined): number | null {
  const day = String(raw || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const target = new Date(`${day}T12:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetDay = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  return Math.round((targetDay.getTime() - today.getTime()) / 86400000);
}

export type ForecastTimingAccent = 'urgent' | 'soon' | 'later';

/** Color and hero label for an alert. Timing (today, coming week) is separate. */
export function severityDisplay(severity: string | null | undefined): {
  label: string;
  accent: ForecastTimingAccent;
} {
  const sev = (severity || '').trim().toLowerCase();
  if (sev === 'high') return { label: 'High', accent: 'urgent' };
  if (sev === 'medium') return { label: 'Medium', accent: 'soon' };
  if (sev === 'low') return { label: 'Low', accent: 'later' };
  if (!sev) return { label: 'Alert', accent: 'later' };
  return {
    label: sev.replace(/\b\w/g, (c) => c.toUpperCase()),
    accent: 'later',
  };
}

export type ForecastTimingInfo = {
  headline: string;
  sub: string;
  accent: ForecastTimingAccent;
  badgeLabel: string;
  badgeIcon: string;
};

/** Timing + urgency accent for weather peak-day cards (rain/heat). */
export function getForecastTimingInfo(raw: string | null | undefined): ForecastTimingInfo {
  const diff = forecastDaysFromToday(raw);
  const pretty = formatForecastAlertDate(raw);
  if (diff == null) {
    return {
      headline: 'UPCOMING',
      sub: pretty ? `Expected on ${pretty}` : 'Expected soon',
      accent: 'later',
      badgeLabel: 'Monitor',
      badgeIcon: 'ri-eye-line',
    };
  }
  if (diff <= 0) {
    return {
      headline: 'TODAY',
      sub: pretty ? `Expected on ${pretty}` : 'Expected today',
      accent: 'urgent',
      badgeLabel: 'Needs attention',
      badgeIcon: 'ri-error-warning-line',
    };
  }
  if (diff === 1) {
    return {
      headline: 'TOMORROW',
      sub: 'Expected tomorrow',
      accent: 'urgent',
      badgeLabel: 'Needs attention',
      badgeIcon: 'ri-error-warning-line',
    };
  }
  if (diff <= 3) {
    return {
      headline: `IN ${diff} DAYS`,
      sub: pretty ? `Expected on ${pretty}` : `In ${diff} days`,
      accent: 'soon',
      badgeLabel: 'Watch closely',
      badgeIcon: 'ri-error-warning-line',
    };
  }
  return {
    headline: `IN ${diff} DAYS`,
    sub: pretty ? `Expected on ${pretty}` : `In ${diff} days`,
    accent: 'later',
    badgeLabel: 'Monitor',
    badgeIcon: 'ri-eye-line',
  };
}

type IndexForecastDirection = 'decreasing' | 'increasing' | 'steady';

function indexForecastDirectionFromMessage(
  message?: string | null
): IndexForecastDirection {
  const m = (message || '').toLowerCase();
  if (m.includes('decreasing') || m.includes('decline') || m.includes('soften')) {
    return 'decreasing';
  }
  if (m.includes('increasing') || m.includes('improve')) {
    return 'increasing';
  }
  return 'steady';
}

/**
 * Vegetation / moisture change slowly — show a clear “coming week” direction,
 * not peak-style exact dates like rain/heat.
 */
export function getIndexForecastTimingInfo(
  variant: 'vegetation' | 'moisture',
  severity?: string | null,
  message?: string | null
): ForecastTimingInfo {
  const direction = indexForecastDirectionFromMessage(message);
  const sev = (severity || '').toLowerCase();
  const isMoisture = variant === 'moisture';
  const subject = isMoisture ? 'Soil moisture' : 'Crop greenness';

  if (direction === 'decreasing') {
    return {
      headline: 'COMING WEEK',
      sub: `${subject} may go down`,
      accent: sev === 'high' ? 'urgent' : 'soon',
      badgeLabel: isMoisture ? 'Getting drier' : 'Getting weaker',
      badgeIcon: 'ri-arrow-down-line',
    };
  }
  if (direction === 'increasing') {
    return {
      headline: 'COMING WEEK',
      sub: `${subject} may improve`,
      accent: 'later',
      badgeLabel: isMoisture ? 'Getting wetter' : 'Getting stronger',
      badgeIcon: 'ri-arrow-up-line',
    };
  }
  return {
    headline: 'COMING WEEK',
    sub: `${subject} may stay about the same`,
    accent: 'later',
    badgeLabel: 'Staying steady',
    badgeIcon: 'ri-subtract-line',
  };
}

/** Month timing for CADI / monthly outlook cards (`YYYY-MM`). */
export function getMonthTimingInfo(ym: string): ForecastTimingInfo {
  const match = ym.match(/^(\d{4})-(\d{2})$/);
  const now = new Date();
  const cur = now.getFullYear() * 12 + (now.getMonth() + 1);
  if (!match) {
    return {
      headline: 'UPCOMING',
      sub: 'Expected soon',
      accent: 'later',
      badgeLabel: 'Monitor',
      badgeIcon: 'ri-eye-line',
    };
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const tgt = year * 12 + month;
  const diff = tgt - cur;
  const pretty = new Date(year, month - 1, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
  if (diff <= 0) {
    return {
      headline: 'THIS MONTH',
      sub: `Expected in ${pretty}`,
      accent: 'urgent',
      badgeLabel: 'Needs attention',
      badgeIcon: 'ri-error-warning-line',
    };
  }
  if (diff === 1) {
    return {
      headline: 'IN 1 MONTH',
      sub: `Expected in ${pretty}`,
      accent: 'soon',
      badgeLabel: 'Watch closely',
      badgeIcon: 'ri-error-warning-line',
    };
  }
  return {
    headline: `IN ${diff} MONTHS`,
    sub: `Expected in ${pretty}`,
    accent: 'later',
    badgeLabel: 'Monitor',
    badgeIcon: 'ri-eye-line',
  };
}

/** Accent from CADI mean (overrides pure timing when drought is severe). */
export function cadiAccentFromMean(mean: number): ForecastTimingAccent {
  if (mean >= 0.65) return 'urgent';
  if (mean >= 0.4) return 'soon';
  return 'later';
}

export function accentBadgeMeta(accent: ForecastTimingAccent): {
  label: string;
  icon: string;
} {
  if (accent === 'urgent') return { label: 'Needs attention', icon: 'ri-error-warning-line' };
  if (accent === 'soon') return { label: 'Watch closely', icon: 'ri-error-warning-line' };
  return { label: 'Monitor', icon: 'ri-eye-line' };
}

/** Short label for chart x-axis / card meta (e.g. Jun 29, Sep 7). */
export function formatForecastChartDate(raw: string | null | undefined): string {
  if (!raw) return '';
  const text = String(raw).trim();
  const isoDay = text.slice(0, 10);
  let parsed =
    /^\d{4}-\d{2}-\d{2}$/.test(isoDay) ? new Date(`${isoDay}T12:00:00`) : new Date(text);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Remove backend Trend / Model suffixes from farmer-facing alert text. */
export function sanitizeForecastAlertMessage(message: string | null | undefined): string {
  let out = (message || '').trim();
  if (!out) return out;

  const cutAtLabel = (text: string, label: string): string => {
    const inline = text.search(new RegExp(`\\s${label}:\\s*`, 'i'));
    if (inline >= 0) return text.slice(0, inline).trim();
    if (new RegExp(`^${label}:\\s*`, 'i').test(text)) return '';
    return text;
  };

  out = cutAtLabel(out, 'Model');
  out = cutAtLabel(out, 'Trend');
  // Fallback if Model/Trend formatting differs from backend templates.
  out = out.replace(/\s*Model:\s*ensemble\s*\([\s\S]*$/i, '').trim();
  out = out.replace(/\s*Trend:\s*[^.]+(?:\.|$)/gi, '').trim();

  out = out.replace(/\s{2,}/g, ' ').replace(/\.\.+$/, '.').trim();
  if (out && !/[.!?]$/.test(out)) {
    out += '.';
  }
  return out;
}

function normalizeForecastAlertRecord(alert: FarmForecastAlertRecord): FarmForecastAlertRecord {
  return {
    ...alert,
    alert_message: sanitizeForecastAlertMessage(alert.alert_message),
  };
}

const RAIN_MM_IN_MESSAGE = /(?:up to|about)\s+(\d+(?:\.\d+)?)\s*mm/i;
const RAIN_CHANCE_IN_MESSAGE = /~?(\d+)\s*%\s*chance/i;

export function rainMillimetresFromMessage(message: string | null | undefined): number | null {
  const match = (message || '').match(RAIN_MM_IN_MESSAGE);
  if (!match) return null;
  const amount = Number(match[1]);
  return Number.isFinite(amount) ? amount : null;
}

function lightRainOutlookMessage(mm: number, chanceLabel: string): string {
  const amount = Math.max(0, Math.round(mm));
  return `About ${amount} mm of light rain is likely (${chanceLabel}). Wait to spray until it passes. A light shower can still rinse a fresh application.`;
}

function rainChanceLabel(alert: FarmForecastAlertRecord): string {
  const fromMessage = (alert.alert_message || '').match(RAIN_CHANCE_IN_MESSAGE);
  if (fromMessage) return `~${fromMessage[1]}% chance`;
  const pop = Number(alert.data_value);
  if (Number.isFinite(pop) && pop >= 0 && pop <= 100) return `~${Math.round(pop)}% chance`;
  return 'rain is likely';
}

/** Same-day heat + light rain should not give opposite advice. */
export function reconcileWeatherOutlooks(
  alerts: FarmForecastAlertRecord[]
): FarmForecastAlertRecord[] {
  const rainyMm = new Map<string, number>();
  for (const alert of alerts) {
    if ((alert.alert_type || '').toLowerCase() !== 'weather_rain') continue;
    const day = String(alert.forecast_date || '').slice(0, 10);
    const mm = rainMillimetresFromMessage(alert.alert_message);
    if (day && mm != null && mm >= 1) rainyMm.set(day, mm);
  }

  const kept: FarmForecastAlertRecord[] = [];
  for (const alert of alerts) {
    const type = (alert.alert_type || '').toLowerCase();
    const day = String(alert.forecast_date || '').slice(0, 10);
    const message = alert.alert_message || '';

    if (type === 'weather_rain') {
      const mm = rainMillimetresFromMessage(message);
      const heavyCopy = /heavy field work|pesticides, fungicides/i.test(message);
      if (mm != null && mm < 10 && heavyCopy) {
        kept.push({
          ...alert,
          severity: 'medium',
          alert_message: lightRainOutlookMessage(mm, rainChanceLabel(alert)),
        });
        continue;
      }
    }

    if (type === 'weather_heat' && rainyMm.has(day)) {
      const temp = Number(alert.data_value);
      if (!Number.isFinite(temp) || temp < 35) continue;
      kept.push({
        ...alert,
        alert_message: message
          .replace(
            /Watch crop water stress and irrigate if needed\.?/i,
            'Rain is also expected, so wait before irrigating.'
          )
          .replace(
            /Watch crop water stress\.?/i,
            'Rain is also expected, so wait before irrigating.'
          ),
      });
      continue;
    }

    kept.push(alert);
  }
  return kept;
}

export function forecastAlertMeasurementLabel(alert: FarmForecastAlertRecord): string | null {
  const type = (alert.alert_type || '').toLowerCase();
  const value = Number(alert.data_value);
  if (type === 'weather_heat' && Number.isFinite(value)) {
    return `${value.toFixed(1)}°C`;
  }
  if (type === 'weather_rain') {
    const mm = rainMillimetresFromMessage(alert.alert_message);
    if (mm == null) return null;
    return `${Math.round(mm)} mm`;
  }
  return null;
}

function normalizeForecastAlertsResponse(
  data: FarmForecastAlertsResponse
): FarmForecastAlertsResponse {
  return {
    farm_id: data.farm_id,
    ndvi_forecast: (data.ndvi_forecast || []).map(normalizeForecastAlertRecord),
    ndmi_forecast: (data.ndmi_forecast || []).map(normalizeForecastAlertRecord),
    weather_risk: reconcileWeatherOutlooks(
      (data.weather_risk || []).map(normalizeForecastAlertRecord)
    ),
  };
}

export type ForecastChartPoint = {
  date: string;
  value: number;
};

/** Build sorted chart points from forecast alert rows (uses data_value + forecast_date). */
export function forecastAlertsToChartPoints(
  alerts: FarmForecastAlertRecord[]
): ForecastChartPoint[] {
  const byDate = new Map<string, number>();
  for (const alert of alerts) {
    const day = String(alert.forecast_date || '').slice(0, 10);
    const val = alert.data_value;
    if (!day || val == null || Number.isNaN(Number(val))) continue;
    byDate.set(day, Number(val));
  }
  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, value]) => ({ date, value }));
}

/** A single forecast point cannot show a trend — require at least two dates. */
export const MIN_FORECAST_TREND_CHART_POINTS = 2;

export function hasForecastTrendChart(alerts: FarmForecastAlertRecord[]): boolean {
  return forecastAlertsToChartPoints(alerts).length >= MIN_FORECAST_TREND_CHART_POINTS;
}

export function formatForecastValue(
  value: number,
  kind: 'ndvi' | 'ndmi' | 'rain_mm' | 'rain_pop' | 'temp_c' | 'weather'
): string {
  if (kind === 'rain_pop') {
    return `${Math.round(value)}%`;
  }
  if (kind === 'rain_mm' || kind === 'weather') {
    return `${Math.round(value)} mm`;
  }
  if (kind === 'temp_c') {
    return `${value.toFixed(1)}°C`;
  }
  return value.toFixed(2);
}

const CHART_ONLY_FORECAST_TYPES = new Set([
  'forecast_trend',
  'rain_trend',
  'heat_trend',
]);

/** True when a stored rain alert is not meaningful (e.g. 0 mm / low chance). */
function isNonActionableWeatherRain(alert: FarmForecastAlertRecord): boolean {
  const type = (alert.alert_type || '').toLowerCase();
  if (type !== 'weather_rain') return false;

  const severity = (alert.severity || '').toLowerCase();
  const message = (alert.alert_message || '').toLowerCase();
  if (severity === 'low' || message.includes('mostly light')) return true;

  // Guard older stored rows: near-zero amount + low probability is not real rain risk.
  const pop = Number(alert.data_value);
  if (Number.isFinite(pop) && pop < 40 && /up to\s+0\s*mm/.test(message)) {
    return true;
  }
  return false;
}

/** Actionable forecast alerts (excludes chart-only / non-meaningful rain rows). */
export function actionableForecastAlerts(alerts: FarmForecastAlertRecord[]): FarmForecastAlertRecord[] {
  return alerts.filter((a) => {
    const type = (a.alert_type || '').toLowerCase();
    if (CHART_ONLY_FORECAST_TYPES.has(type)) return false;
    if (isNonActionableWeatherRain(a)) return false;
    return true;
  });
}

export type OutboundMobileAlertPayload = {
  farm_id: number;
  alert_type: string;
  severity: 'high' | 'medium' | 'low';
  alert_message: string;
  data_value: string;
  observation_date: string;
  auto_send: boolean;
  force_send: boolean;
  source: string;
};

type FarmAlertTone = 'attention' | 'positive' | 'neutral';

function getFarmAlertTone(message: string): FarmAlertTone {
  const m = message.toLowerCase();
  if (m.includes('attention')) return 'attention';
  if (m.includes('good news')) return 'positive';
  return 'neutral';
}

function toneToSeverity(tone: FarmAlertTone): OutboundMobileAlertPayload['severity'] {
  if (tone === 'attention') return 'high';
  if (tone === 'positive') return 'low';
  return 'medium';
}

function formatObservationDate(raw: string | null | undefined, message: string): string {
  if (raw) {
    const d = String(raw).slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  }
  const iso = message.match(/(\d{4}-\d{2}-\d{2})/);
  if (iso?.[1]) return iso[1];
  return new Date().toISOString().slice(0, 10);
}

export function buildOutboundMobileAlertPayload(
  kind: 'ndvi' | 'ndmi',
  record: FarmIndexAlertRecord,
  farmId: number | string
): OutboundMobileAlertPayload {
  const tone = getFarmAlertTone(record.alert_message);
  const alert_type =
    kind === 'ndmi' ? (tone === 'attention' ? 'drought' : 'moisture') : 'vegetation';
  const indexValue = kind === 'ndvi' ? record.ndvi_value : record.ndmi_value;
  const data_value =
    indexValue != null && !Number.isNaN(Number(indexValue))
      ? String(Number(indexValue))
      : '';

  return {
    farm_id: Number(farmId),
    alert_type,
    severity: toneToSeverity(tone),
    alert_message: record.alert_message.trim(),
    data_value,
    observation_date: formatObservationDate(record.observation_date, record.alert_message),
    auto_send: true,
    force_send: true,
    source: 'third_party',
  };
}

function normalizeForecastSeverity(
  severity: string | null | undefined
): OutboundMobileAlertPayload['severity'] {
  const value = (severity || 'medium').toLowerCase();
  if (value === 'high' || value === 'medium' || value === 'low') return value;
  return 'medium';
}

export function buildOutboundForecastAlertPayload(
  alert: FarmForecastAlertRecord,
  farmId: number | string
): OutboundMobileAlertPayload {
  const message = sanitizeForecastAlertMessage(alert.alert_message);
  const data_value =
    alert.data_value != null && !Number.isNaN(Number(alert.data_value))
      ? String(Number(alert.data_value))
      : '';

  return {
    farm_id: Number(farmId),
    alert_type: (alert.alert_type || 'vegetation').toLowerCase(),
    severity: normalizeForecastSeverity(alert.severity),
    alert_message: message,
    data_value,
    observation_date: formatObservationDate(alert.forecast_date, message),
    auto_send: true,
    force_send: true,
    source: 'third_party',
  };
}

/** Build an outbound mobile alert payload for a farm's CADI drought status. */
export function buildCADIAlertPayload(
  farmId: number | string,
  cadiValue: number,
  monthLabel: string
): OutboundMobileAlertPayload {
  const level =
    cadiValue < 0.4 ? 'No drought' :
    cadiValue < 0.55 ? 'Dry' :
    cadiValue < 0.65 ? 'Moderate drought' :
    cadiValue < 0.75 ? 'Severe drought' :
    cadiValue < 0.85 ? 'Extreme drought' : 'Exceptional drought';

  const severity: OutboundMobileAlertPayload['severity'] =
    cadiValue >= 0.65 ? 'high' : cadiValue >= 0.4 ? 'medium' : 'low';

  const alert_message =
    `Drought Outlook – ${monthLabel}: CADI index is ${cadiValue.toFixed(3)}, ` +
    `indicating ${level} conditions for this farm.`;

  return {
    farm_id: Number(farmId),
    alert_type: 'drought',
    severity,
    alert_message,
    data_value: String(cadiValue.toFixed(3)),
    observation_date: new Date().toISOString().slice(0, 10),
    auto_send: true,
    force_send: true,
    source: 'third_party',
  };
}

/** Accent from pest occurrence probability (0–1). */
export function pestAccentFromMean(mean: number): ForecastTimingAccent {
  if (mean >= 0.6) return 'urgent';
  if (mean >= 0.4) return 'soon';
  return 'later';
}

export function pestSeverityLabel(mean: number): string {
  if (mean < 0.2) return 'Very low';
  if (mean < 0.4) return 'Low';
  if (mean < 0.6) return 'Moderate';
  if (mean < 0.8) return 'High';
  return 'Very high';
}

/** Build an outbound mobile alert payload for farm pest risk (manual send). */
export function buildPestAlertPayload(
  farmId: number | string,
  pestLabel: string,
  meanValue: number,
  _quarterLabel?: string
): OutboundMobileAlertPayload {
  const level = pestSeverityLabel(meanValue);
  const severity: OutboundMobileAlertPayload['severity'] =
    meanValue >= 0.6 ? 'high' : meanValue >= 0.4 ? 'medium' : 'low';

  const alert_message =
    `Pest Risk – ${pestLabel}: occurrence probability is ` +
    `${meanValue.toFixed(3)}, indicating ${level.toLowerCase()} risk for this farm.`;

  return {
    farm_id: Number(farmId),
    alert_type: 'pest',
    severity,
    alert_message,
    data_value: String(meanValue.toFixed(3)),
    // Daily-looking observation date so mobile "last updated" feels recent
    observation_date: new Date().toISOString().slice(0, 10),
    auto_send: true,
    force_send: true,
    source: 'third_party',
  };
}

export function collectOutboundMobileAlertPayloads(
  farmId: number | string,
  ndvi: FarmIndexAlertRecord | null,
  ndmi: FarmIndexAlertRecord | null
): OutboundMobileAlertPayload[] {
  const payloads: OutboundMobileAlertPayload[] = [];
  if (ndvi?.alert_message?.trim()) {
    payloads.push(buildOutboundMobileAlertPayload('ndvi', ndvi, farmId));
  }
  if (ndmi?.alert_message?.trim()) {
    payloads.push(buildOutboundMobileAlertPayload('ndmi', ndmi, farmId));
  }
  return payloads;
}

const OUTBOUND_ALERTS_SEND_URL = `${DIGISAKA_OUTBOUND_API_ORIGIN}/api/outbound/alerts/send`;
const OUTBOUND_ALERT_FEEDBACKS_URL = `${DIGISAKA_OUTBOUND_API_ORIGIN}/api/outbound/alert-feedbacks`;

export type AlertFeedbackInterventionData = {
  effect?: string | null;
  rating?: string | number | null;
  details?: string | null;
  action_date?: string | null;
  actions_taken?: unknown;
  action_taken?: unknown;
  actions?: unknown;
  action?: unknown;
  actions_planned?: unknown;
  followed_recommendation?: string | null;
  response_plan?: string | null;
  feedback_stage?: string | null;
  field_observation?: string | null;
  forecast_date?: string | null;
  forecast_kind?: string | null;
  photos?: unknown;
  proof_photos?: unknown;
};

export type AlertFeedbackRecord = {
  id: number;
  alert_id: number;
  user_id: number;
  farm_id: number;
  alert_type: string;
  data_value?: string | null;
  is_accurate?: boolean | string | null;
  is_helpful?: boolean | string | null;
  rating?: number | null;
  comment?: string | null;
  source?: string | null;
  proof_photos?: unknown;
  photos?: unknown;
  images?: unknown;
  action?: unknown;
  actions?: unknown;
  action_taken?: unknown;
  actions_taken?: unknown;
  action_date?: string | null;
  intervention_data?: AlertFeedbackInterventionData | string | null;
  latitude?: string | number | null;
  longitude?: string | number | null;
  farmer_id?: number | null;
  created_at: string;
  updated_at?: string;
  alert?: {
    id: number;
    farm_id: number;
    alert_type: string;
    data_value?: number | null;
    alert_message?: string | null;
    observation_date?: string | null;
    created_at?: string;
  } | null;
  user?: {
    id: number;
    first_name?: string | null;
    last_name?: string | null;
    full_name?: string | null;
    email?: string | null;
    mobile?: string | null;
  } | null;
  farm?: {
    id?: number;
    name?: string | null;
    farm_unique_id?: string | null;
    total_land_area?: string | number | null;
  } | null;
};

const INTERVENTION_CODE_LABELS: Record<string, string> = {
  bumuti: 'Improved',
  lumala: 'Worsened',
  walang_pagbabago: 'No change',
  oo: 'Yes',
  hindi: 'No',
  bahagya: 'Partially',
  yes: 'Yes',
  no: 'No',
  true: 'Yes',
  false: 'No',
  before_forecast: 'Before the forecast event',
  after_forecast: 'After the forecast event',
  rain: 'Rain',
  heat: 'Heat',
  naagapan: 'Addressed in time',
};

/** Farmer-app practice options are stored in Filipino; WebGIS shows English. */
const INTERVENTION_ACTION_LABELS: Record<string, string> = {
  'nag-inspeksyon ng dahon': 'Inspected leaves',
  'nag inspeksyon ng dahon': 'Inspected leaves',
  'nag-adjust ng iskedyul ng patubig': 'Adjusted irrigation schedule',
  'nag adjust ng iskedyul ng patubig': 'Adjusted irrigation schedule',
  'leads anti-stress foliar (unang opsyon)': 'Applied anti-stress foliar (first option)',
  'leads anti-stress foliar': 'Applied anti-stress foliar',
  'linisin ang kanal at daluyan ng tubig': 'Clean canals and waterways',
  'linisin ang kanal': 'Clean canals and waterways',
};

function normalizeInterventionKey(raw: string): string {
  return raw
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[-–—]/g, '-')
    .replace(/[“”"'`]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

const FEEDBACK_STAGE_LABELS: Record<string, string> = {
  before_forecast: 'Before the forecast event',
  after_forecast: 'After the forecast event',
  before: 'Before',
  after: 'After',
};

const FORECAST_KIND_LABELS: Record<string, string> = {
  rain: 'Rain',
  heat: 'Heat',
  drought: 'Drought',
  ndvi: 'Vegetation',
  ndmi: 'Moisture',
};

const FEEDBACK_SOURCE_LABELS: Record<string, string> = {
  ndvi_intervention: 'Vegetation intervention',
  drought_intervention: 'Drought intervention',
  pest_intervention: 'Pest intervention',
  weather_rain_intervention: 'Rain intervention',
  weather_heat_intervention: 'Heat intervention',
  other_alerts: 'Alert feedback',
};

function shiftIsoDay(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  dt.setDate(dt.getDate() + days);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function todayLocalIso(): string {
  const dt = new Date();
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

export function formatInterventionLabel(raw: string | number | null | undefined): string {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  const key = normalizeInterventionKey(s);
  const exact =
    INTERVENTION_CODE_LABELS[key] ||
    INTERVENTION_ACTION_LABELS[key] ||
    FEEDBACK_STAGE_LABELS[key] ||
    FORECAST_KIND_LABELS[key];
  if (exact) return exact;
  for (const [filipino, english] of Object.entries(INTERVENTION_ACTION_LABELS)) {
    if (key === filipino) return english;
    if (key.includes(filipino) || (key.length >= 18 && filipino.includes(key))) {
      return english;
    }
  }
  return s;
}

export function formatFeedbackFlag(value: boolean | string | number | null | undefined): string | null {
  if (value === true || value === 1 || value === '1') return 'Yes';
  if (value === false || value === 0 || value === '0') return 'No';
  if (typeof value === 'string') {
    const key = value.trim().toLowerCase();
    if (!key) return null;
    if (['yes', 'true', 'oo', '1'].includes(key)) return 'Yes';
    if (['no', 'false', 'hindi', '0'].includes(key)) return 'No';
    if (['bahagya', 'partial', 'partially'].includes(key)) return 'Partially';
    return formatInterventionLabel(value);
  }
  return null;
}

export function formatFeedbackSource(source: string | null | undefined): string | null {
  const key = String(source || '').trim();
  if (!key) return null;
  return FEEDBACK_SOURCE_LABELS[key] || key.replace(/_/g, ' ');
}

const FEEDBACK_ALERT_TYPE_LABELS: Record<string, string> = {
  vegetation: 'Vegetation',
  moisture: 'Moisture',
  drought: 'Drought',
  pest: 'Pest',
  ndvi: 'Vegetation (NDVI)',
  ndmi: 'Moisture (NDMI)',
};

export function formatFeedbackAlertType(alertType: string | null | undefined): string | null {
  const key = String(alertType || '').trim();
  if (!key) return null;
  return FEEDBACK_ALERT_TYPE_LABELS[key.toLowerCase()] || key.replace(/_/g, ' ');
}

function parseCoordNumber(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/^(null|undefined|n\/?a|none|-)$/i.test(trimmed)) return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  return null;
}

/** OpenLayers-style `[lon, lat]`, or null when the API did not send a geotag. */
export function parseFeedbackLonLat(
  latitude: unknown,
  longitude: unknown
): [number, number] | null {
  const lat = parseCoordNumber(latitude);
  const lng = parseCoordNumber(longitude);
  if (lat == null || lng == null) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  if (lat === 0 && lng === 0) return null;
  return [lng, lat];
}

export function getFeedbackLonLat(feedback: AlertFeedbackRecord): [number, number] | null {
  return parseFeedbackLonLat(feedback.latitude, feedback.longitude);
}

export function formatFeedbackGps(
  latitude: unknown,
  longitude: unknown
): string | null {
  const pair = parseFeedbackLonLat(latitude, longitude);
  if (!pair) return null;
  return `${pair[1].toFixed(5)}, ${pair[0].toFixed(5)}`;
}

function parseMaybeJson(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

export function parseFeedbackInterventionData(
  feedback: AlertFeedbackRecord
): AlertFeedbackInterventionData | null {
  const parsed = parseMaybeJson(feedback.intervention_data);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  return parsed as AlertFeedbackInterventionData;
}

function collectActionStrings(value: unknown, into: string[]): void {
  const parsed = parseMaybeJson(value);
  if (parsed == null || parsed === '') return;
  if (typeof parsed === 'string' || typeof parsed === 'number' || typeof parsed === 'boolean') {
    const text = String(parsed).trim();
    if (!text) return;
    if (text.includes('|')) {
      text.split('|').forEach((part) => collectActionStrings(part, into));
      return;
    }
    if (text.includes(';')) {
      text.split(';').forEach((part) => collectActionStrings(part, into));
      return;
    }
    into.push(text);
    return;
  }
  if (Array.isArray(parsed)) {
    parsed.forEach((item) => collectActionStrings(item, into));
    return;
  }
  if (typeof parsed === 'object') {
    const row = parsed as Record<string, unknown>;
    const direct =
      row.actions_taken ??
      row.action_taken ??
      row.actions ??
      row.action ??
      row.name ??
      row.label ??
      row.title ??
      row.value ??
      row.text ??
      row.description;
    if (direct != null) {
      collectActionStrings(direct, into);
      return;
    }
    Object.values(row).forEach((item) => {
      if (typeof item === 'string' || Array.isArray(item) || (item && typeof item === 'object')) {
        collectActionStrings(item, into);
      }
    });
  }
}

export function getFeedbackActionsTaken(feedback: AlertFeedbackRecord): string[] {
  const intervention = parseFeedbackInterventionData(feedback);
  const collected: string[] = [];
  collectActionStrings(intervention?.actions_taken, collected);
  collectActionStrings(intervention?.action_taken, collected);
  collectActionStrings(intervention?.actions, collected);
  collectActionStrings(intervention?.action, collected);
  collectActionStrings(feedback.actions_taken, collected);
  collectActionStrings(feedback.action_taken, collected);
  collectActionStrings(feedback.actions, collected);
  collectActionStrings(feedback.action, collected);
  return uniqueActionLabels(collected);
}

export function getFeedbackActionsPlanned(feedback: AlertFeedbackRecord): string[] {
  const intervention = parseFeedbackInterventionData(feedback);
  const collected: string[] = [];
  collectActionStrings(intervention?.actions_planned, collected);
  collectActionStrings((feedback as { actions_planned?: unknown }).actions_planned, collected);
  return uniqueActionLabels(collected);
}

function uniqueActionLabels(collected: string[]): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const item of collected) {
    const label = formatInterventionLabel(item);
    const key = label.toLowerCase();
    if (!key || seen.has(key)) continue;
    if (key === 'action_date' || /^\d{4}-\d{2}-\d{2}$/.test(key) || /^\d+(\.\d+)?$/.test(key)) continue;
    if (INTERVENTION_CODE_LABELS[key]) continue;
    seen.add(key);
    unique.push(label);
  }
  return unique;
}

export function getFeedbackProofPhotos(feedback: AlertFeedbackRecord): string[] {
  const intervention = parseFeedbackInterventionData(feedback);
  const buckets = [
    feedback.proof_photos,
    feedback.photos,
    feedback.images,
    intervention?.proof_photos,
    intervention?.photos,
  ];
  const urls: string[] = [];
  const seen = new Set<string>();

  const push = (raw: unknown) => {
    const parsed = parseMaybeJson(raw);
    if (parsed == null || parsed === '') return;
    if (Array.isArray(parsed)) {
      parsed.forEach(push);
      return;
    }
    if (typeof parsed === 'object') {
      const row = parsed as Record<string, unknown>;
      push(row.url ?? row.path ?? row.file ?? row.src ?? row.photo ?? row.image);
      return;
    }
    const text = String(parsed).trim();
    if (!text || seen.has(text)) return;
    seen.add(text);
    urls.push(text);
  };

  buckets.forEach(push);
  return urls;
}

export function getFeedbackActionDate(feedback: AlertFeedbackRecord): string | null {
  const intervention = parseFeedbackInterventionData(feedback);
  return (
    normalizeObservationDay(intervention?.action_date) ||
    normalizeObservationDay(feedback.action_date) ||
    normalizeObservationDay(feedback.created_at) ||
    normalizeObservationDay(feedback.alert?.observation_date)
  );
}

export function feedbackHasIntervention(feedback: AlertFeedbackRecord): boolean {
  if (getFeedbackActionsTaken(feedback).length > 0) return true;
  const source = String(feedback.source || '').toLowerCase();
  return source.includes('intervention');
}

export type FeedbackTimeSeriesPlan = {
  index: 'ndvi' | 'ndmi';
  startDate: string;
  endDate: string;
  actionDate: string;
  indexLabel: string;
};

export function buildFeedbackTimeSeriesPlan(
  feedback: AlertFeedbackRecord,
  today = todayLocalIso()
): FeedbackTimeSeriesPlan {
  const actionDate = getFeedbackActionDate(feedback) || today;
  let startDate = shiftIsoDay(actionDate, -30);
  let endDate = shiftIsoDay(actionDate, 60);
  if (endDate > today) endDate = today;
  if (startDate > endDate) startDate = endDate;

  const type = String(feedback.alert_type || feedback.alert?.alert_type || '').toLowerCase();
  const source = String(feedback.source || '').toLowerCase();
  const moistureLike =
    type.includes('moisture') ||
    type.includes('ndmi') ||
    type.includes('drought') ||
    source.includes('drought');

  const index: 'ndvi' | 'ndmi' = moistureLike ? 'ndmi' : 'ndvi';
  return {
    index,
    startDate,
    endDate,
    actionDate,
    indexLabel: index.toUpperCase(),
  };
}

export type InterventionCompareScene = {
  date: string;
  mean_index_value?: number | null;
  cloud_percentage?: number;
  cloud_cover?: number;
  tile_url?: string;
  [key: string]: unknown;
};

export type InterventionComparePair = {
  near: InterventionCompareScene;
  after: InterventionCompareScene;
  nearCloud: number | null;
  afterCloud: number | null;
  nearDate: string;
  afterDate: string;
};

function sceneDay(scene: { date?: string }): string | null {
  return normalizeObservationDay(scene.date);
}

function sceneCloud(scene: { cloud_percentage?: number; cloud_cover?: number }): number {
  const value = scene.cloud_percentage ?? scene.cloud_cover;
  return typeof value === 'number' && Number.isFinite(value) ? value : 100;
}

function pickClearestScene(
  scenes: InterventionCompareScene[],
  targetDay: string
): InterventionCompareScene | null {
  if (scenes.length === 0) return null;
  const targetMs = Date.parse(`${targetDay}T12:00:00`);
  const preferredCloud = 30;
  return [...scenes].sort((a, b) => {
    const aCloud = sceneCloud(a);
    const bCloud = sceneCloud(b);
    const aPref = aCloud <= preferredCloud ? 0 : 1;
    const bPref = bCloud <= preferredCloud ? 0 : 1;
    if (aPref !== bPref) return aPref - bPref;
    if (Math.abs(aCloud - bCloud) > 5) return aCloud - bCloud;
    const aDay = sceneDay(a);
    const bDay = sceneDay(b);
    const aDist = aDay ? Math.abs(Date.parse(`${aDay}T12:00:00`) - targetMs) : Number.POSITIVE_INFINITY;
    const bDist = bDay ? Math.abs(Date.parse(`${bDay}T12:00:00`) - targetMs) : Number.POSITIVE_INFINITY;
    return aDist - bDist;
  })[0];
}

/** Clearest scene on/near the action date, plus the clearest scene after it. */
export function pickInterventionComparePair(
  results: InterventionCompareScene[] | undefined,
  actionDate: string
): InterventionComparePair | null {
  const actionDay = normalizeObservationDay(actionDate);
  if (!actionDay || !results?.length) return null;
  const actionMs = Date.parse(`${actionDay}T12:00:00`);
  if (Number.isNaN(actionMs)) return null;

  const usable = results.filter((scene) => {
    const day = sceneDay(scene);
    return Boolean(day && scene.tile_url);
  });

  const nearCandidates = usable.filter((scene) => {
    const day = sceneDay(scene);
    if (!day) return false;
    return Date.parse(`${day}T12:00:00`) <= actionMs;
  });
  const afterCandidates = usable.filter((scene) => {
    const day = sceneDay(scene);
    if (!day) return false;
    return Date.parse(`${day}T12:00:00`) > actionMs;
  });

  const near = pickClearestScene(nearCandidates, actionDay);
  const afterTarget = shiftIsoDay(actionDay, 10);
  const after = pickClearestScene(afterCandidates, afterTarget);
  if (!near || !after) return null;

  const nearDay = sceneDay(near);
  const afterDay = sceneDay(after);
  if (!nearDay || !afterDay || nearDay >= afterDay) return null;

  const nearCloudRaw = sceneCloud(near);
  const afterCloudRaw = sceneCloud(after);
  return {
    near,
    after,
    nearDate: nearDay,
    afterDate: afterDay,
    nearCloud: nearCloudRaw >= 100 ? null : nearCloudRaw,
    afterCloud: afterCloudRaw >= 100 ? null : afterCloudRaw,
  };
}

export function summarizeSeriesAroundAction(
  results: Array<{ date?: string; mean_index_value?: number | null }> | undefined,
  actionDate: string
): {
  beforeMean: number;
  afterMean: number;
  delta: number;
  beforeCount: number;
  afterCount: number;
} | null {
  if (!results?.length) return null;
  const actionMs = Date.parse(`${actionDate}T12:00:00`);
  if (Number.isNaN(actionMs)) return null;

  const before: number[] = [];
  const after: number[] = [];
  for (const row of results) {
    const day = normalizeObservationDay(row.date);
    if (!day) continue;
    const value = row.mean_index_value;
    if (typeof value !== 'number' || Number.isNaN(value)) continue;
    const t = Date.parse(`${day}T12:00:00`);
    if (Number.isNaN(t)) continue;
    if (t < actionMs) before.push(value);
    else after.push(value);
  }
  if (before.length === 0 || after.length === 0) return null;
  const avg = (xs: number[]) => xs.reduce((sum, n) => sum + n, 0) / xs.length;
  const beforeMean = avg(before);
  const afterMean = avg(after);
  return {
    beforeMean,
    afterMean,
    delta: afterMean - beforeMean,
    beforeCount: before.length,
    afterCount: after.length,
  };
}

type AlertFeedbacksPaginatedData = {
  data: AlertFeedbackRecord[];
  current_page?: number;
  last_page?: number;
  per_page?: number;
  total?: number;
};

export type MatchedFarmAlertFeedbacks = {
  vegetation: AlertFeedbackRecord[];
  moisture: AlertFeedbackRecord[];
};

export function normalizeObservationDay(date: string | null | undefined): string | null {
  if (!date) return null;
  const day = String(date).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null;
}

function normalizeAlertMessage(message: string | null | undefined): string {
  return (message ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function extractObservationDaysFromMessage(message: string): string[] {
  const matches = message.match(/\d{4}-\d{2}-\d{2}/g) ?? [];
  return [...new Set(matches.filter((day) => /^\d{4}-\d{2}-\d{2}$/.test(day)))];
}

function observationDaysWithinTolerance(
  leftDay: string,
  rightDay: string,
  toleranceDays = 1
): boolean {
  const left = Date.parse(`${leftDay}T12:00:00Z`);
  const right = Date.parse(`${rightDay}T12:00:00Z`);
  if (Number.isNaN(left) || Number.isNaN(right)) return false;
  const diffDays = Math.abs(left - right) / (24 * 60 * 60 * 1000);
  return diffDays <= toleranceDays;
}

export type AlertMatchCriteria = {
  alert_type: string;
  observation_days: string[];
  alert_message: string;
};

export function getAlertMatchCriteria(
  kind: 'ndvi' | 'ndmi',
  record: FarmIndexAlertRecord
): AlertMatchCriteria {
  const payload = buildOutboundMobileAlertPayload(kind, record, 0);
  const observationDays = new Set<string>();

  const rawDay = normalizeObservationDay(record.observation_date);
  if (rawDay) observationDays.add(rawDay);

  const payloadDay = normalizeObservationDay(payload.observation_date);
  if (payloadDay) observationDays.add(payloadDay);

  for (const day of extractObservationDaysFromMessage(record.alert_message)) {
    observationDays.add(day);
  }

  return {
    alert_type: payload.alert_type,
    observation_days: [...observationDays],
    alert_message: record.alert_message.trim(),
  };
}

export function feedbackMatchesAlertCriteria(
  feedback: AlertFeedbackRecord,
  criteria: AlertMatchCriteria
): boolean {
  const feedbackType = feedback.alert_type ?? feedback.alert?.alert_type;
  if (feedbackType !== criteria.alert_type) return false;

  const feedbackDay = normalizeObservationDay(feedback.alert?.observation_date);
  if (
    feedbackDay &&
    criteria.observation_days.some(
      (day) => day === feedbackDay || observationDaysWithinTolerance(day, feedbackDay)
    )
  ) {
    return true;
  }

  const feedbackMessage = normalizeAlertMessage(
    sanitizeForecastAlertMessage(feedback.alert?.alert_message)
  );
  const criteriaMessage = normalizeAlertMessage(
    sanitizeForecastAlertMessage(criteria.alert_message)
  );
  if (feedbackMessage && criteriaMessage && feedbackMessage === criteriaMessage) {
    return true;
  }

  return false;
}

export function filterFeedbacksForAlert(
  feedbacks: AlertFeedbackRecord[],
  kind: 'ndvi' | 'ndmi',
  record: FarmIndexAlertRecord
): AlertFeedbackRecord[] {
  const criteria = getAlertMatchCriteria(kind, record);
  return feedbacks.filter((feedback) => feedbackMatchesAlertCriteria(feedback, criteria));
}

export function getForecastAlertMatchCriteria(alert: FarmForecastAlertRecord): AlertMatchCriteria {
  const payload = buildOutboundForecastAlertPayload(alert, 0);
  const observationDays = new Set<string>();

  const forecastDay = normalizeObservationDay(alert.forecast_date);
  if (forecastDay) observationDays.add(forecastDay);

  const payloadDay = normalizeObservationDay(payload.observation_date);
  if (payloadDay) observationDays.add(payloadDay);

  for (const day of extractObservationDaysFromMessage(alert.alert_message)) {
    observationDays.add(day);
  }

  return {
    alert_type: payload.alert_type,
    observation_days: [...observationDays],
    alert_message: sanitizeForecastAlertMessage(alert.alert_message),
  };
}

export function filterFeedbacksForForecastAlert(
  feedbacks: AlertFeedbackRecord[],
  alert: FarmForecastAlertRecord
): AlertFeedbackRecord[] {
  const criteria = getForecastAlertMatchCriteria(alert);
  return feedbacks.filter((feedback) => feedbackMatchesAlertCriteria(feedback, criteria));
}

export function matchFeedbacksToFarmAlerts(
  feedbacks: AlertFeedbackRecord[],
  ndvi: FarmIndexAlertRecord | null,
  ndmi: FarmIndexAlertRecord | null
): MatchedFarmAlertFeedbacks {
  return {
    vegetation: ndvi ? filterFeedbacksForAlert(feedbacks, 'ndvi', ndvi) : [],
    moisture: ndmi ? filterFeedbacksForAlert(feedbacks, 'ndmi', ndmi) : [],
  };
}

export function resolveAlertFeedbackPhotoUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const normalized = path.replace(/^\/+/, '');
  return `${DIGISAKA_OUTBOUND_API_ORIGIN}/storage/${normalized}`;
}

export async function fetchAlertFeedbacksByFarmId(
  farmId: number | string,
  perPage = 50
): Promise<AlertFeedbackRecord[]> {
  const headers = getDigisakaAuthHeaders();
  const response = await axios.get<{ success?: boolean; data?: AlertFeedbacksPaginatedData }>(
    OUTBOUND_ALERT_FEEDBACKS_URL,
    {
      headers,
      params: {
        farm_id: Number(farmId),
        per_page: perPage,
      },
    }
  );

  const payload = response.data?.data;
  if (!payload || !Array.isArray(payload.data)) {
    throw new Error('Unexpected alert feedback response');
  }

  return payload.data;
}

export async function fetchMatchedAlertFeedbacks(
  farmId: number | string,
  ndvi: FarmIndexAlertRecord | null,
  ndmi: FarmIndexAlertRecord | null,
  perPage = 50
): Promise<MatchedFarmAlertFeedbacks> {
  const feedbacks = await fetchAlertFeedbacksByFarmId(farmId, perPage);
  return matchFeedbacksToFarmAlerts(feedbacks, ndvi, ndmi);
}

export async function sendFarmAlertsToMobileApp(
  payloads: OutboundMobileAlertPayload[]
): Promise<void> {
  if (payloads.length === 0) {
    throw new Error('No alerts to send');
  }

  const headers = {
    ...getDigisakaAuthHeaders(),
    'Content-Type': 'application/json',
  };

  await Promise.all(
    payloads.map((body) => axios.post(OUTBOUND_ALERTS_SEND_URL, body, { headers }))
  );
}
